// src/components/ui/VisualElement.tsx
import React from 'react';
import { VisualElementV10 } from '@dopomogai/types';
import { MessageCircle, Package } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

interface VisualElementProps {
  visual: VisualElementV10;
  className?: string;
  onChatClick?: () => void;
  showChatButton?: boolean;
  isThumbnail?: boolean;
  src?: string; 
}

export const VisualElement: React.FC<VisualElementProps> = ({
  visual,
  className = '',
  onChatClick,
  showChatButton = false,
  isThumbnail = false,
  src
}) => {
  const { t } = useTranslation('common');

  const chatButtonClasses = clsx(
    "absolute bottom-3 right-3 text-white p-2.5 rounded-lg border-2 border-black",
    "shadow-[2px_2px_0_0_black] hover:shadow-[3px_3px_0_0_black]",
    "transform transition-all duration-150 ease-out",
    "hover:-translate-y-0.5 active:translate-y-0 active:shadow-[1px_1px_0_0_black]",
    "[.theme-liquid-glass_&]:border [.theme-liquid-glass_&]:border-white/20",
    "[.theme-liquid-glass_&]:backdrop-blur-sm [.theme-liquid-glass_&]:shadow-none",
    "[.theme-liquid-glass_&]:hover:translate-y-0 [.theme-liquid-glass_&]:hover:shadow-none",
    visual.type === 'video_url' || visual.type === 'interactive_animation_url'
      ? 'bg-teal-500 hover:bg-teal-400 active:bg-teal-600'
      : 'bg-emerald-500 hover:bg-emerald-400 active:bg-emerald-600'
  );

  

  const altText = visual.altText || t('visualElement.defaultAltText', { type: visual.type });
  const shouldShowChat = showChatButton && onChatClick && !isThumbnail;

  switch (visual.type) {
    case 'image':
      return (
        <div className={clsx("relative w-full h-full", className)}>
          <img
            src={src || (isThumbnail ? (visual.thumbnailUrl || visual.url) : visual.url)}
            alt={altText}
            className="w-full h-full object-cover"
            loading="lazy" // Add lazy loading for images
          />
          {shouldShowChat && (
            <button
              onClick={onChatClick}
              className={chatButtonClasses}
              aria-label={t('visualElement.chatButtonAriaLabel')}
            >
              <MessageCircle size={20} className="drop-shadow-sm" />
            </button>
          )}
        </div>
      );

    case 'video_url':
      return (
        <div className={clsx("relative w-full h-full bg-black [.theme-liquid-glass_&]:bg-slate-900/70", className)}>
          <video
            src={src || visual.url}
            // Use poster for both main view and thumbnail
            poster={visual.posterUrl || visual.thumbnailUrl}
            // Apply logic based on isThumbnail
            controls={!isThumbnail}
            autoPlay={!isThumbnail && (visual.autoplay || false)}
            loop={!isThumbnail && (visual.autoplay || false)}
            muted={true} // Muting is best practice for autoplay and often required
            playsInline // Essential for mobile browsers
            className="w-full h-full object-contain" // Use 'contain' for videos to avoid cropping
            // For thumbnails, we can prevent the browser from downloading video data
            preload={isThumbnail ? "metadata" : "auto"}
          >
            {t('visualElement.videoNotSupported')}
          </video>
          {shouldShowChat && (
            <button
              onClick={onChatClick}
              className={chatButtonClasses}
              aria-label={t('visualElement.chatButtonAriaLabel')}
            >
              <MessageCircle size={20} className="drop-shadow-sm" />
            </button>
          )}
        </div>
      );

    case '3d_model_url':
      // Thumbnails for 3D models should just be their poster image.
      if (isThumbnail) {
        return (
          <div className={clsx("relative w-full h-full", className)}>
             <img
              src={src || visual.posterUrl || visual.thumbnailUrl} // Prioritize override, then poster, then thumbnail
              alt={altText}
              className="w-full h-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
              <Package size={24} className="text-white/80" />
            </div>
          </div>
        )
      }
      // Main viewer for 3D model
      return (
        <div className={clsx("relative w-full h-full bg-slate-100 flex items-center justify-center [.theme-liquid-glass_&]:bg-slate-900/60", className)}>
          {/* In a real app, you would swap this div with a <model-viewer> component */}
          {visual.posterUrl && (
            <img src={src || visual.posterUrl} alt={altText} className="w-full h-full object-cover opacity-70" />
          )}
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/30 p-4 [.theme-liquid-glass_&]:bg-black/40">
            <Package size={32} className="text-white mb-2" />
            <p className="text-white text-lg font-semibold text-center">{t('visualElement.3dModelViewerTitle')}</p>
            <p className="text-slate-200 text-xs text-center">({t('visualElement.3dModelPlaceholderText')})</p>
          </div>
          {shouldShowChat && (
            <button onClick={onChatClick} className={chatButtonClasses} aria-label={t('visualElement.chatButtonAriaLabel')}>
              <MessageCircle size={20} className="drop-shadow-sm" />
            </button>
          )}
        </div>
      );

    case 'interactive_animation_url':
      // For thumbnails, show a poster image instead of a heavy iframe.
      if (isThumbnail) {
        return (
          <div className={clsx("relative w-full h-full", className)}>
             <img
              src={src || visual.posterUrl || visual.thumbnailUrl}
              alt={altText}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        )
      }
      return (
        <div className={clsx("w-full h-full relative", className)}>
          <iframe
            src={src || visual.url}
            title={altText}
            className="w-full h-full border-0"
            allowFullScreen
            sandbox="allow-scripts allow-same-origin"
          ></iframe>
          {shouldShowChat && (
            <button onClick={onChatClick} className={chatButtonClasses} aria-label={t('visualElement.chatButtonAriaLabel')}>
              <MessageCircle size={20} className="drop-shadow-sm" />
            </button>
          )}
        </div>
      );

    default:
      return (
        <div className={clsx("w-full h-full bg-slate-200 flex items-center justify-center p-4 [.theme-liquid-glass_&]:bg-white/5", className)}>
          <p className="text-slate-500 text-center [.theme-liquid-glass_&]:text-white/60">{t('visualElement.unavailable')}</p>
        </div>
      );
  }
};

export default VisualElement;
